import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  ChatCircle,
  Clock,
  Paperclip,
  UserCircle,
  WarningCircle,
  CheckCircle,
  Ticket,
} from '@phosphor-icons/react'

import { useLang } from '../../context/LangContext'
import { getTicketById } from '../../services/ticketService'
import StatusBadge from '../../components/Badge/StatusBadge'

const DOT_CLASS = {
  reply: 'timeline__dot--reply',
  status: 'timeline__dot--status',
  created: 'timeline__dot--created',
  resolved: 'timeline__dot--resolved',
}

export default function TicketDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t } = useLang()
  const ticket = getTicketById(id)
  const [reply, setReply] = useState('')

  if (!ticket) {
    return (
      <div className="page">
        <p>Ticket not found.</p>
      </div>
    )
  }

  const statusLabel = {
    new: t('statusNew'),
    open: t('statusOpen'),
    in_progress: t('statusInProgress'),
    resolved: t('statusResolved'),
  }[ticket.status]

  return (
    <div className="page page--wide ticket-detail-page">
      <button className="breadcrumb ticket-detail-back" onClick={() => navigate('/tickets')}>
        <ArrowLeft size={14} />
        <span>{t('backToTickets')}</span>
        <span className="breadcrumb__sep">/</span>
        <span className="breadcrumb__current">{ticket.ref}</span>
      </button>

      <section className="ticket-detail-hero ticket-detail-hero--standard">
        <div className="ticket-detail-hero__icon">
          <Ticket size={30} />
        </div>

        <div className="ticket-detail-hero__main">
          <div className="ticket-detail-hero__ref">{ticket.ref}</div>
          <h1 className="ticket-detail-hero__title">{ticket.title}</h1>
          <div className="ticket-detail-hero__meta">
            <StatusBadge status={ticket.status} labelOverride={statusLabel} />
            <span>{ticket.category}</span>
            <span>•</span>
            <span>{ticket.subCategory}</span>
            <span>•</span>
            <span>{t('openedAgo')} {ticket.openedAgo}</span>
          </div>
        </div>

        <div className="ticket-detail-hero__status">
          <span className="ticket-detail-hero__progress-label">{t('resProgress')}</span>
          <strong>{ticket.progressPct}%</strong>
          <div className="progress-bar progress-bar--compact">
            <div className="progress-bar__fill" style={{ width: `${ticket.progressPct}%` }} />
          </div>
        </div>
      </section>

      <div className="ticket-detail-layout ticket-detail-layout--standard">
        <main className="ticket-detail-layout__main">
          <section className="card ticket-section-card ticket-activity-card">
            <div className="section__header ticket-card-header">
              <div>
                <h2 className="section__title">{t('activity')}</h2>
                <p>Follow every status change and staff reply for this ticket.</p>
              </div>
            </div>

            <div className="timeline timeline--card ticket-timeline">
              {ticket.timeline.map((ev, i) => (
                <div className="timeline__item ticket-timeline__item" key={i}>
                  <div className="timeline__track">
                    <div className={`timeline__dot ${DOT_CLASS[ev.type] ?? 'timeline__dot--created'}`} />
                    {i < ticket.timeline.length - 1 && <div className="timeline__line" />}
                  </div>

                  <div className="timeline__body ticket-timeline__body">
                    <div className="timeline__label">{ev.label}</div>
                    {ev.body && <div className="timeline__message">{ev.body}</div>}
                    <div className="timeline__time">{ev.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>


          <section className="card ticket-section-card ticket-attachments-card">
            <div className="ticket-card-header ticket-card-header--inline">
              <div>
                <h2 className="section__title">Attachments</h2>
                <p>Click a file to preview. Use the download icon to save the file.</p>
              </div>

              <span className="attachment-count">2 files</span>
            </div>

            <div className="attachment-thumb-grid">
              <button type="button" className="attachment-thumb-card" aria-label="Preview network-access-point.png">
                <div className="attachment-thumb-card__preview attachment-thumb-card__preview--image">
                  <img
                    src="https://placehold.co/300x300/e8f2fb/1a6fb5?text=IMG"
                    alt="network-access-point.png"
                  />
                </div>

                <span className="attachment-thumb-card__overlay">
                  network-access-point.png
                </span>

                <span
                  className="attachment-thumb-card__download"
                  onClick={(e) => e.stopPropagation()}
                  title="Download"
                >
                  ↓
                </span>
              </button>

              <button type="button" className="attachment-thumb-card" aria-label="Preview incident-report.pdf">
                <div className="attachment-thumb-card__preview attachment-thumb-card__preview--pdf">
                  PDF
                </div>

                <span className="attachment-thumb-card__overlay">
                  incident-report.pdf
                </span>

                <span
                  className="attachment-thumb-card__download"
                  onClick={(e) => e.stopPropagation()}
                  title="Download"
                >
                  ↓
                </span>
              </button>
            </div>
          </section>

          <section className="card ticket-section-card ticket-reply-card">
            <div className="ticket-card-header">
              <div>
                <h2 className="section__title">{t('addReply')}</h2>
                <p>Send additional information or follow up with the support team.</p>
              </div>
            </div>

            <textarea
              className="form-textarea ticket-reply-box"
              rows={4}
              placeholder={t('replyPlaceholder')}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
            />

            <div className="form-actions ticket-reply-actions">
              <button className="btn btn--ghost"><Paperclip size={15} /> {t('attach')}</button>
              <button className="btn btn--primary"><ChatCircle size={15} /> {t('sendReply')}</button>
            </div>
          </section>
        </main>

        <aside className="ticket-detail-layout__side">
          <section className="card ticket-summary-card ticket-summary-card--standard">
            <h2 className="section__title">{t('ticketSummary')}</h2>

            <div className="summary-list">
              <div className="summary-list__item">
                <div className="summary-list__icon"><UserCircle size={18} /></div>
                <div>
                  <div className="summary-list__label">{t('assignedTo')}</div>
                  <div className="summary-list__value">
                    {ticket.assignee ? ticket.assignee.name : '—'}
                  </div>
                </div>
              </div>

              <div className="summary-list__item">
                <div className="summary-list__icon"><Clock size={18} /></div>
                <div>
                  <div className="summary-list__label">{t('expectedRes')}</div>
                  <div className="summary-list__value">{ticket.expectedResolution ?? '—'}</div>
                </div>
              </div>

              <div className="summary-list__item">
                <div className="summary-list__icon"><WarningCircle size={18} /></div>
                <div>
                  <div className="summary-list__label">{t('priority')}</div>
                  <div className="summary-list__value summary-list__value--capitalize">{ticket.priority}</div>
                </div>
              </div>

              <div className="summary-list__item">
                <div className="summary-list__icon"><CheckCircle size={18} /></div>
                <div>
                  <div className="summary-list__label">{t('resProgress')}</div>
                  <div className="summary-list__value">{ticket.progressPct}%</div>
                </div>
              </div>
            </div>
          </section>
        </aside>
      </div>
    </div>
  )
}
